import { Target, Heart, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const OurStory = () => {
  const cards = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Our Mission',
      description:
        'To preserve Bengali craftsmanship by connecting artisans with conscious consumers who value authenticity, quality, and cultural heritage.',
      chip: 'from-clay-100 to-clay-200 text-clay-600',
      card: 'from-clay-50 to-cream-100 border-clay-100',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Our Values',
      description:
        'Authenticity, sustainability, fair trade, and community empowerment guide everything we do at তন্তিকা.',
      chip: 'from-saffron-100 to-saffron-200 text-saffron-700',
      card: 'from-saffron-50 to-cream-100 border-saffron-100',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Our Vision',
      description:
        'To become the most trusted platform for authentic Bengali crafts, creating sustainable livelihoods for artisans while enriching homes with cultural heritage.',
      chip: 'from-rose-100 to-rose-200 text-rose-600',
      card: 'from-rose-50 to-cream-100 border-rose-100',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-cocoa-900">Our Journey</h2>
              <div className="space-y-6">
                <p className="text-cocoa-600 text-lg leading-relaxed">
                  Founded in 2024, তন্তিকা was born from a simple idea: to preserve and promote the
                  exquisite craftsmanship of Bengal in the digital age. Our founders, passionate about
                  traditional arts, noticed that many skilled artisans struggled to reach a wider audience.
                </p>
                <p className="text-cocoa-600 text-lg leading-relaxed">
                  We started with just 5 artisans from rural Bengal and have grown into a platform
                  that showcases the best of Bengali craftsmanship to customers across India and beyond.
                </p>
                <div className="bg-cream-100 border-l-4 border-clay-500 rounded-r-2xl p-5">
                  <p className="text-cocoa-800 text-lg italic leading-relaxed">
                    Every product on তন্তিকা tells a story - of tradition, skill, and the hands that
                    carefully crafted it. We're not just selling products; we're sharing heritage.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Mission/Vision/Values */}
            <div className="space-y-6">
              {cards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${card.card} p-7 rounded-2xl border hover:shadow-warm transition-shadow`}
                >
                  <div className="flex items-center mb-4">
                    <div className={`bg-gradient-to-br ${card.chip} p-3 rounded-xl mr-4`}>
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-bold text-cocoa-900">{card.title}</h3>
                  </div>
                  <p className="text-cocoa-600 leading-relaxed">{card.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStory;
